import { useState } from "react"
import { View, ActivityIndicator, Pressable } from "react-native"
import { Map as GMap, APIProvider, useMap } from "@vis.gl/react-google-maps"
import { Text, Button, MCIcon, ScrollView } from "@src/components"
import { ar } from "@src/i18n"
import { Polygon } from "./polygon"
import { toCoordinates } from "./utils"
import { priceFromDBFormat, trpc, type RouterOutputs } from "@src/utils"
import { DeliveryAreaForm } from "./delivery-area-form"
import { Show, useObservable } from "@legendapp/state/react"
import type { Observable } from "@legendapp/state"

export type EditingState = { type: "adding" } | { type: "editing"; id: number }

export function DeliveryAreasView() {
  const [mapCenter] = useState({ lat: 31.4318395, lng: 31.6799195 })
  const editing$ = useObservable<EditingState | undefined>()

  return (
    <APIProvider apiKey="AIzaSyCzy3G3BtLgXN4QA14kGVT2d1VjXS06VP4">
      <GMap defaultZoom={15} defaultCenter={mapCenter} disableDefaultUI mapTypeControl gestureHandling="greedy">
        <DeliveryAreasMap editing$={editing$} />
      </GMap>
    </APIProvider>
  )
}

function DeliveryAreasMap({ editing$ }: { editing$: Observable<EditingState | undefined> }) {
  const map = useMap()
  const areas = trpc.deliveryAreas.list.useQuery()

  function handleEdit(area: RouterOutputs["deliveryAreas"]["list"][number]) {
    editing$.set({ type: "editing", id: area.id })
    const lat = area.minLat + (area.maxLat - area.minLat) / 2
    const lng = area.minLng + (area.maxLng - area.minLng) / 2
    map?.panTo({ lat, lng })
  }

  function handleStop() {
    editing$.set(undefined)
  }

  return (
    <>
      {areas.data?.map((area) => (
        <Show
          key={area.id}
          if={() => {
            const v = editing$.get()
            return v?.type !== "editing" || v?.id !== area.id
          }}
        >
          <Polygon points={toCoordinates(area.geojson)} fill={area.fill ?? "#000"} onClick={() => handleEdit(area)} />
        </Show>
      ))}

      <Show if={editing$}>
        {() => {
          const value = editing$.get()!
          return (
            <DeliveryAreaForm
              key={value.type === "editing" ? `edit-${value.id}` : "adding"}
              mode={value.type === "adding" ? "add" : "edit"}
              editId={value.type === "editing" ? value.id : undefined}
              onDone={handleStop}
            />
          )
        }}
      </Show>

      <DeliveryAreasList editing$={editing$} onAdd={() => editing$.set({ type: "adding" })} onEdit={handleEdit} />
    </>
  )
}

type DALProps = {
  editing$: Observable<EditingState | undefined>
  onAdd: () => void
  onEdit: (area: RouterOutputs["deliveryAreas"]["list"][number]) => void
}
function DeliveryAreasList({ editing$, onAdd, onEdit }: DALProps) {
  const map = useMap()
  const areas = trpc.deliveryAreas.list.useQuery()
  const deleteMutation = trpc.deliveryAreas.delete.useMutation()
  const utils = trpc.useUtils()
  const editing = editing$.get()

  async function handleDelete(areaId: number) {
    await deleteMutation.mutateAsync({ id: areaId }, { onSuccess: () => utils.deliveryAreas.list.invalidate() })
  }

  function handlePanAndEdit(area: RouterOutputs["deliveryAreas"]["list"][number]) {
    onEdit(area)
    const lat = area.minLat + (area.maxLat - area.minLat) / 2
    const lng = area.minLng + (area.maxLng - area.minLng) / 2
    map?.panTo({ lat, lng })
  }

  return (
    <View className="absolute bottom-2 right-2 w-xs bg-white rounded shadow-lg border border-gray-200 overflow-hidden h-96">
      <View className="p-3 border-b border-gray-200 flex-row justify-between items-center bg-gray-50">
        <Text className="font-bold text-gray-800">{ar.deliveryAreas}</Text>
        <Button className="w-8 h-8 p-0 bg-blue-500 rounded-full items-center justify-center" onPress={onAdd}>
          <MCIcon name="plus" size={20} color="white" />
        </Button>
      </View>

      <ScrollView>
        {areas.isPending && <ActivityIndicator className="my-4" />}
        {areas.data?.map((area) => {
          const isEditing = editing?.type === "editing" && editing.id === area.id
          return (
            <View key={area.id} className={`border-b border-gray-100 flex-row items-center ${isEditing ? "bg-blue-50" : ""}`}>
              <Pressable className="flex-1 flex-row items-center gap-x-2" onPress={() => handlePanAndEdit(area)}>
                <View className="w-4 h-4 rounded-full ms-2" style={{ backgroundColor: area.fill ?? "#000" }} />
                <Text className="flex-1 text-gray-700 font-medium text-right" numberOfLines={1}>
                  {area.name}
                </Text>
                <Text className="text-gray-400 text-xs">{area.fees ? priceFromDBFormat(area.fees) : 0}</Text>
              </Pressable>
              <Pressable className="py-3 px-2" onPress={() => handlePanAndEdit(area)}>
                <MCIcon name="pencil" size={20} color="#666" />
              </Pressable>
              <Pressable className="py-3 px-2" onPress={() => handleDelete(area.id)}>
                <MCIcon name="delete" size={20} color="#dc2626" />
              </Pressable>
            </View>
          )
        })}
      </ScrollView>
    </View>
  )
}
